import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import type { TripChatMobileTabId } from './TripChatMobileTabs'
import {
  areSearchStringsEqual,
  buildSearchWithTripChatMobileView,
  getTripChatMobileViewUrlState,
} from './tripChatMobileViewUrl'

interface UseTripChatMobileViewUrlReturn {
  activeTab: TripChatMobileTabId
  setActiveTab: (tab: TripChatMobileTabId) => void
}

/**
 * Mobile-only Chat/Timeline/List/Map persistence via `?view=`.
 * Uses history `replace` so tab changes are shareable/refresh-safe without stacking Back entries.
 */
export default function useTripChatMobileViewUrl(): UseTripChatMobileViewUrlReturn {
  const location = useLocation()
  const navigate = useNavigate()
  const urlState = useMemo(
    () => getTripChatMobileViewUrlState(location.search),
    [location.search],
  )
  const [activeTab, setActiveTabState] = useState<TripChatMobileTabId>(urlState.tab)

  const replaceViewInUrl = useCallback((tab: TripChatMobileTabId) => {
    const nextSearch = buildSearchWithTripChatMobileView(location.search, tab)
    if (areSearchStringsEqual(nextSearch, location.search)) {
      return
    }

    navigate(
      {
        pathname: location.pathname,
        search: nextSearch,
        hash: location.hash,
      },
      { replace: true, state: location.state },
    )
  }, [location.hash, location.pathname, location.search, location.state, navigate])

  useEffect(() => {
    setActiveTabState(previous => (previous === urlState.tab ? previous : urlState.tab))

    if (urlState.shouldNormalizeUrl) {
      replaceViewInUrl(urlState.tab)
    }
  }, [replaceViewInUrl, urlState.shouldNormalizeUrl, urlState.tab])

  const setActiveTab = useCallback((tab: TripChatMobileTabId) => {
    setActiveTabState(tab)
    replaceViewInUrl(tab)
  }, [replaceViewInUrl])

  return {
    activeTab,
    setActiveTab,
  }
}
